/**
 * Nối dịch vụ của gói (ADR-0019 mục 2.1) với điều hướng và quyền giao diện của
 * cổng tenant (ADR-0034).
 *
 * Không khai bảng nào bằng tay. Ba nguồn đều đã có:
 *
 * - Dịch vụ ↔ nhóm điều hướng: trường `dichVu` của từng nhóm ở `nav-config.ts`.
 * - Đường dẫn → phân hệ RBAC: `TIEN_TO_PHAN_HE` ở `route-permissions.ts`.
 * - Dịch vụ → `vung_api`: danh mục dịch vụ của `goi-dich-vu`.
 *
 * Phép tính nằm ở `modules/auth/nghiep-vu/quyen-cong-tenant.ts`; tệp này chỉ
 * dựng tham số cho nó từ registry đang chạy.
 */
import { NAV_SECTIONS } from '../components/layout/nav-config.ts';
import type { NavItem } from '../components/layout/nav-config.ts';
import { dichVuCuaDuongDan, lapDichVuTheoDoan } from '../modules/auth/nghiep-vu/quyen-cong-tenant.ts';
import type { BanDoDichVu, DichVuVung } from '../modules/auth/nghiep-vu/types.ts';
import { DANH_MUC } from '../modules/goi-dich-vu/nghiep-vu/danh-muc.ts';
import { tach } from './duong-dan-cong.ts';
import { TIEN_TO_PHAN_HE } from './route-permissions.ts';

const hrefCua = (items: readonly NavItem[]): string[] =>
  items.flatMap((m) => [...(m.href ? [m.href] : []), ...hrefCua(m.children ?? [])]);

/** Bản đồ đường dẫn → dịch vụ → phân hệ của cổng tenant. */
export const BAN_DO_DICH_VU: BanDoDichVu = {
  dichVuTheoDoan: lapDichVuTheoDoan(
    NAV_SECTIONS.filter((s) => s.phamVi === 'tenant').map((s) => ({
      dichVu: s.dichVu,
      hrefs: hrefCua(s.items),
    })),
  ),
  phanHeTheoTienTo: TIEN_TO_PHAN_HE,
};

/**
 * Danh mục dịch vụ cùng `vung_api` của từng dịch vụ.
 *
 * Đọc hằng số ở `modules/goi-dich-vu/nghiep-vu/danh-muc.ts` chứ không gọi
 * `GET /api/v1/dich-vu`: quyền điều hướng tính đồng bộ lúc dựng khung, chờ mạng ở
 * đây sẽ làm thanh điều hướng nhảy sau mỗi lần tải. Hằng số đó có test canh khớp
 * `docs/goi-dich-vu/fixture-chuan.json`, nên không lệch với backend được (ADR-0050).
 */
export const DANH_MUC_DICH_VU: readonly DichVuVung[] = DANH_MUC;

/**
 * Người này có được vào nhánh của đường dẫn hay không, xét theo dịch vụ.
 *
 * `dichVu` để trống — tài khoản mẫu, nhân sự nền tảng đang xem giao diện
 * tenant — thì không lọc. Nhánh không thuộc dịch vụ nào cũng không lọc.
 */
export function duocVaoDichVu(pathname: string, dichVu: readonly string[] | undefined): boolean {
  if (!dichVu) return true;
  const dv = dichVuCuaDuongDan(tach(pathname).goc, BAN_DO_DICH_VU.dichVuTheoDoan);
  return dv === undefined || dichVu.includes(dv);
}
